// SCR-MAIL и MOD-MAIL-LETTER (E09-T04; ux/03 §10): список писем, вложения, «Забрать» и «Забрать всё (N)».
// Тема — ключ `titleKey` с параметрами письма, текст — `<titleKey без -SUBJECT>-BODY`, если такой ключ есть.
// Письма создаёт только сервер; новое — WS `mail.new` (счётчик конверта на SCR-HOME).
import { useEffect, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { I } from '../assets/icons.js';
import { duration, mskTime } from '../i18n/format.js';
import { hasKey, t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { onWs } from '../net/ws.js';
import { online, setWallet } from '../state/game.js';
import { mailUnclaimed } from '../state/quests.js';
import { storage } from '../state/storage.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { ErrorBanner, ScreenTitle, Sheet, Skeleton, useActionBar, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { Typed } from '../ui/text-fx.js';
import { flyGrant } from '../ui/fx.js';
import { GrantIcons } from './quests.js';

type Mail = ResponseOf<Api['getMail']>['items'][number];

const READ_KEY = 'mailRead';
const readSet = () => new Set(storage.getJson<string[]>(READ_KEY) ?? []);
const markRead = (id: string) => { const s = readSet(); s.add(id); storage.setJson(READ_KEY, [...s].slice(-200)); };

export const hasAttach = (m: Mail) => !!(Object.keys(m.attachments.currencies ?? {}).length || m.attachments.items?.length);
export const claimable = (m: Mail, now: number) => hasAttach(m) && !m.claimedAt && (!m.expiresAt || Date.parse(m.expiresAt) > now);

export function mailTitle(m: Mail): string {
  return hasKey(m.titleKey) ? tr(m.titleKey, m.params) : tr('CL-MAIL-UNTITLED');
}
export function mailBody(m: Mail): string | null {
  const k = `${m.titleKey.replace(/-SUBJECT$/, '')}-BODY`;
  return hasKey(k) ? tr(k, m.params) : null;
}
/** Отправитель по виду письма (ux/03 §10). */
const SENDER: Record<string, string> = { uncle: 'CL-MAIL-FROM-UNCLE', coop_board: 'CL-MAIL-FROM-COOP', boss_pot: 'CL-MAIL-FROM-COOP', quests: 'CL-MAIL-FROM-SEMYONYCH', system: 'CL-MAIL-FROM-ADMIN' };

let mailCache: Mail[] | null = null;

export function MailScreen(_p: ScreenProps) {
  const app = useApp();
  const now = useNow(60_000);
  const [items, setItems] = useState<Mail[] | null>(mailCache);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [read, setRead] = useState(readSet);
  const load = async () => {
    setErr(false);
    try {
      const r = await app.call('getMail');
      mailCache = r.items;
      setItems(r.items);
      mailUnclaimed.value = r.unclaimed;
    } catch { setErr(true); }
  };
  useEffect(() => { void load(); }, []);
  useEffect(() => onWs((ev) => { if (ev.t === 'mail.new') void load(); }), []);
  const ready = (items ?? []).filter((m) => claimable(m, now));
  const claimAll = async () => {
    setBusy(true);
    let got = 0;
    for (const m of ready) {
      try { const r = await app.call('claimMail', { params: { mailId: m.mailId }, body: {} }); setWallet(r.wallet); got++; }
      catch (e) { if (!(e instanceof ApiCallError && (e.code === 'ALREADY_CLAIMED' || e.code === 'GONE'))) { app.handleError(e); break; } }
    }
    if (got) { app.haptic('success'); showToast(tr('CL-MAIL-GOT-ALL', { n: got }), 'success'); }
    setBusy(false);
    void load();
  };
  useActionBar({ back: true, ...(ready.length ? { main: { text: tr('CL-MAIL-CLAIM-ALL', { n: ready.length }), testId: 'mail-claim-all', disabled: busy || !online.value, onClick: claimAll } } : {}) });
  const open = (m: Mail) => {
    markRead(m.mailId);
    setRead(readSet());
    app.nav.openSheet('MOD-MAIL-LETTER', { mail: m, onClaimed: () => { void load(); } });
  };
  return (
    <>
      <ScreenTitle>{tr('CL-MAIL-TITLE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-MAIL">
        <div class="page col">
          {err && !items ? <ErrorBanner what={tr('CL-MAIL-TITLE')} onRetry={() => { void load(); }} /> : null}
          {!items && !err ? <Skeleton h={56} n={4} /> : null}
          {items && !items.length ? <div class="empty" data-testid="mail-empty"><I n="mail" size={48} class="ill" />{tr('CL-MAIL-EMPTY')}</div> : null}
          {items?.map((m) => {
            const unread = !read.has(m.mailId) && !m.claimedAt;
            const left = m.expiresAt ? Date.parse(m.expiresAt) - now : null;
            return (
              <button key={m.mailId} class={`list-row mail-row ${unread ? 'unread' : ''}`} data-testid={`mail-${m.mailId}`} onClick={() => open(m)}>
                <span class="col" style={{ flex: 1, gap: 2, minWidth: 0 }}>
                  <span class="small muted">{tr(SENDER[m.kind] ?? 'CL-MAIL-FROM-ADMIN')} · {mskDate(Date.parse(m.createdAt))}</span>
                  <span class="ellipsis">{mailTitle(m)}</span>
                  {left !== null && left > 0 && !m.claimedAt && hasAttach(m) ? <span class="small muted">{tr('CL-MAIL-LEFT', { time: left > 2 * 86_400_000 ? tr('CL-MAIL-DAYS', { n: Math.floor(left / 86_400_000) }) : duration(left) })}</span> : null}
                </span>
                {hasAttach(m) ? (m.claimedAt ? <I n="check" size={18} /> : <I n="gift" size={22} />) : null}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function mskDate(ms: number): string {
  const d = new Date(ms).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' });
  return `${d} ${mskTime(ms)}`;
}

export function MailLetterSheet({ entry }: SheetProps) {
  const app = useApp();
  const [m, setM] = useState(entry.params['mail'] as Mail);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const now = Date.now();
  const body = mailBody(m);
  const claim = async () => {
    setBusy(true);
    try {
      const r = await app.call('claimMail', { params: { mailId: m.mailId }, body: {} });
      flyGrant(document.querySelector('[data-testid="mail-claim"]'), m.attachments?.currencies);
      setWallet(r.wallet);
      app.haptic('success');
      setM({ ...m, claimedAt: new Date().toISOString() });
      mailUnclaimed.value = Math.max(0, mailUnclaimed.value - 1);
      (entry.params['onClaimed'] as (() => void) | undefined)?.();
      showToast(tr('CL-MAIL-GOT'), 'success');
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : null;
      if (code === 'ALREADY_CLAIMED') { setNote(tr('CL-MAIL-ALREADY')); setM({ ...m, claimedAt: m.claimedAt ?? new Date().toISOString() }); }
      else if (code === 'GONE') setNote(tr('CL-MAIL-EXPIRED'));
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  const can = claimable(m, now);
  return (
    <Sheet entry={entry} title={mailTitle(m)} testId="MOD-MAIL-LETTER" actions={can ? [{ text: tr('CL-MAIL-CLAIM'), testId: 'mail-claim', disabled: busy || !online.value, onClick: claim }] : [{ text: tr('UI-BTN-CLOSE'), tone: 'outline', onClick: () => app.nav.popSheet() }]}>
      <div class="col">
        <span class="small muted">{tr(SENDER[m.kind] ?? 'CL-MAIL-FROM-ADMIN')} · {mskDate(Date.parse(m.createdAt))}</span>
        {body ? <p class="letter" style={{ margin: 0, whiteSpace: 'pre-wrap' }}><Typed text={body} cps={90} /></p> : null}
        {hasAttach(m) ? (
          <div class="col" style={{ gap: 4 }}>
            <b>{tr('CL-MAIL-ATTACH')}</b>
            <GrantIcons g={m.attachments} size={20} />
            {m.claimedAt ? <span class="small muted" data-testid="mail-claimed">{tr('CL-MAIL-CLAIMED')}</span> : null}
            {!m.claimedAt && m.expiresAt && Date.parse(m.expiresAt) <= now ? <span class="small danger-text">{tr('CL-MAIL-EXPIRED')}</span> : null}
          </div>
        ) : null}
        {note ? <span class="small danger-text" data-testid="mail-note">{note}</span> : null}
      </div>
    </Sheet>
  );
}
